import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRoomId as useRoomIdImport } from '../../hooks/useRoomId';
import { useViewBox } from '../../hooks/useViewBox';
import { createCanvasSession } from '../../services/CanvasSession';

import { CommandManager } from '../../commands/CommandManager';
import {
  AppState,
  Coordinate,
  LineSegment,
  SelectionCoordinates,
  SelectionMode,
  SelectionModes,
  initialSelectionCoordinates,
  initialState,
} from '../../types';
import { useToast } from '../Toast/useToast';
import { AppStateContext } from './AppStateContext';
import { ElementStateContext } from './ElementStateContext';

type AppStateProviderProps = {
  children: React.ReactNode;
  useRoomId?: typeof useRoomIdImport;
  exportToken?: string;
};

const EMPTY_SNAP_POINTS: Coordinate[] = [];
const EMPTY_SNAP_LINES: LineSegment[] = [];

const resolveSetStateAction = <T,>(action: React.SetStateAction<T>, prev: T) =>
  typeof action === 'function' ? (action as (prev: T) => T)(prev) : action;

export const AppStateProvider = ({
  useRoomId = useRoomIdImport,
  exportToken,
  children,
}: AppStateProviderProps) => {
  const canvasId = useRoomId();
  const [appState, setAppState] = useState<AppState>(initialState);
  const { addToast } = useToast();
  const [pendingCount, setPendingCount] = useState(0);
  const [isCanvasReady, setIsCanvasReady] = useState(false);
  const [loadProgress, setLoadProgress] = useState(0);

  const [session] = useState(() =>
    createCanvasSession({
      appState,
      setAppState,
      canvasId,
      addToast,
      exportToken,
      onSyncStatusChange: setPendingCount,
      onCanvasReady: () => setIsCanvasReady(true),
      onLoadProgress: setLoadProgress,
    })
  );
  const [selectedElements, setSelectedElements] = useState<string[]>([]);
  const [hoverElement, setHoverElement] = useState<string | null>(null);
  const [selectionCoordinates, setSelectionCoordinates] =
    useState<SelectionCoordinates>(initialSelectionCoordinates);
  const [selectionMode, setSelectionMode] = useState<SelectionMode>({
    type: SelectionModes.None,
  });
  const [showDebugger] = useState(false);
  const [showAllSnaplines, setShowAllSnaplines] = useState(false);
  const [perpendicularGuideEnabled, setPerpendicularGuideEnabled] =
    useState(false);
  const [showTriangleAngles, setShowTriangleAngles] = useState(false);
  const [viewBox, setViewBox] = useViewBox();
  const [isSpacePressed, setIsSpacePressed] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [isShiftPressed, setIsShiftPressed] = useState(false);
  const [isMetaPressed, setIsMetaPressed] = useState(false);
  // Kept in a ref rather than state: it is written on every mousemove but no
  // component renders from it (only paste/duplicate positioning reads it).
  const cursorPositionRef = useRef<Coordinate>({ x: 0, y: 0 });
  const setCursorPosition = useCallback((coordinate: Coordinate) => {
    cursorPositionRef.current = coordinate;
  }, []);
  const [snapPoints, setSnapPointsState] =
    useState<Coordinate[]>(EMPTY_SNAP_POINTS);
  const [snapLines, setSnapLinesState] =
    useState<LineSegment[]>(EMPTY_SNAP_LINES);
  const [commandManager] = useState(() => new CommandManager(session));
  // Mirrors the latest elements so element renderers can read them from event
  // handlers without subscribing to appState. Assigning during render is safe:
  // it is idempotent and only ever moves forward.
  const elementsRef = useRef(appState.elements);
  elementsRef.current = appState.elements;
  const selectedAnchorRef = useRef(selectionCoordinates.selectedAnchor);
  selectedAnchorRef.current = selectionCoordinates.selectedAnchor;
  const [draggingDimensionId, setDraggingDimensionId] = useState<string | null>(
    null
  );

  // Clearing snapping happens on nearly every mousemove of a drag. Keep the
  // previous array when it is already empty so React can bail out of the render.
  const setSnapPoints: React.Dispatch<React.SetStateAction<Coordinate[]>> =
    useCallback((action) => {
      setSnapPointsState((prev) => {
        const next = resolveSetStateAction(action, prev);
        return prev.length === 0 && next.length === 0 ? prev : next;
      });
    }, []);
  const setSnapLines: React.Dispatch<React.SetStateAction<LineSegment[]>> =
    useCallback((action) => {
      setSnapLinesState((prev) => {
        const next = resolveSetStateAction(action, prev);
        return prev.length === 0 && next.length === 0 ? prev : next;
      });
    }, []);

  useEffect(() => {
    return () => {
      session.cleanup();
    };
  }, [session]);

  const elementState = useMemo(
    () => ({
      commandManager,
      draggingDimensionId,
      selectedElements,
      selectionMode,
      perpendicularGuideEnabled,
      showTriangleAngles,
      elementsRef,
      selectedAnchorRef,
      setSelectedElements,
      setSelectionMode,
    }),
    [
      commandManager,
      draggingDimensionId,
      selectedElements,
      selectionMode,
      perpendicularGuideEnabled,
      showTriangleAngles,
    ]
  );

  const appStateValue = useMemo(
    () => ({
      appState,
      session,
      selectedElements,
      setSelectedElements,
      hoverElement,
      setHoverElement,
      selectionCoordinates,
      setSelectionCoordinates,
      selectionMode,
      setSelectionMode,
      showDebugger,
      showAllSnaplines,
      setShowAllSnaplines,
      perpendicularGuideEnabled,
      setPerpendicularGuideEnabled,
      showTriangleAngles,
      setShowTriangleAngles,
      viewBox,
      setViewBox,
      isSpacePressed,
      setIsSpacePressed,
      isMouseDown,
      setIsMouseDown,
      isShiftPressed,
      setIsShiftPressed,
      isMetaPressed,
      setIsMetaPressed,
      cursorPositionRef,
      setCursorPosition,
      snapPoints,
      setSnapPoints,
      snapLines,
      setSnapLines,
      commandManager,
      draggingDimensionId,
      setDraggingDimensionId,
      pendingCount,
      isCanvasReady,
      loadProgress,
    }),
    [
      appState,
      session,
      selectedElements,
      hoverElement,
      selectionCoordinates,
      selectionMode,
      showDebugger,
      showAllSnaplines,
      perpendicularGuideEnabled,
      showTriangleAngles,
      viewBox,
      setViewBox,
      isSpacePressed,
      isMouseDown,
      isShiftPressed,
      isMetaPressed,
      setCursorPosition,
      snapPoints,
      setSnapPoints,
      snapLines,
      setSnapLines,
      commandManager,
      draggingDimensionId,
      pendingCount,
      isCanvasReady,
      loadProgress,
    ]
  );

  if (!session) {
    return <p>Loading...</p>;
  }

  return (
    <AppStateContext.Provider value={appStateValue}>
      <ElementStateContext.Provider value={elementState}>
        {children}
      </ElementStateContext.Provider>
    </AppStateContext.Provider>
  );
};
